// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FancyScroll from "@eisinc/App/Components/FancyScroll/FancyScroll";
import Menu, { MenuVariants } from "@eisinc/MobiusOverrides/Menu";
import Tooltip from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import { MainNavigationStyles, MappedLink } from "@eisinc/Overrides/Widgets/Header/MainNavigation";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import { Clickable, Typography } from "@insite/mobius/index";
import Link, { StyledIcon } from "@insite/mobius/Link";
import Page, { PageProps } from "@insite/mobius/Page";
import Popover from "@insite/mobius/Popover";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

// <XNG-Changes>
export interface MainNavigationItemStyles {
    popup?: InjectableCss;
    container?: PageProps;
    firstLevel?: InjectableCss;
    itemLink?: InjectableCss;
    menuItemIcon?: IconProps;
}

export const mainNavigationItemStyles: MainNavigationItemStyles = {};

const mapStateToProps = (state: ApplicationState) => ({});

const mapDispatchToProps = {
    loadProductInfoList,
};
// </XNG-Changes>

interface OwnProps {
    link: MappedLink;
    index: number;
    styles: MainNavigationStyles;
    container: React.RefObject<HTMLElement | null>;
    isOpen: boolean;
    displayChangeCustomerLink: boolean;
    // XNG-Change
    menuVariant?: MenuVariants;
}

// <XNG-Changes>
interface OwnState {
    current: number;
    link: MappedLink;
    isFullWidthOpened: boolean;
}
// </XNG-Changes>

type Props = ThemeProps<BaseTheme> &
    OwnProps &
    // XNG-Changes
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

// XNG-Changes : add OwnState
class MainNavigationItem extends React.Component<Props, OwnState> {
    element = React.createRef<HTMLElement>();
    popover = React.createRef<any>();
    // <XNG-Changes>
    timeoutId?: NodeJS.Timeout;
    realTimeTriggered = false;

    constructor(props: any) {
        super(props);

        this.state = {
            current: 0,
            link: {} as MappedLink,
            isFullWidthOpened: false,
        };
    }
    // </XNG-Changes>

    closePopover = () => {
        this.popover.current && (this.popover.current! as any).closePopover();
    };

    openPopover = () => {
        this.popover.current && (this.popover.current! as any).openPopover();
    };
    // <XNG-Changes>

    openMenu = (idx: number, link: MappedLink = {} as MappedLink) => {
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
            this.setState({ current: idx, link });
        }, 150);
        this.popover.current && (this.popover.current! as any).openPopover();
    };

    closeMenu = () => {
        clearTimeout(this.timeoutId);
    };

    closeAllPopupsHandler = (event: any) => {
        // on qty and add to cart for featured products event -> keep the overlay opened
        if (event.target.closest("input") || event.target.closest("button")) {
            return;
        }

        this.setState({ current: 0, link: {} as MappedLink });
    };

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (this.props.isOpen && !prevProps.isOpen) {
            this.openPopover();
        } else if (!this.props.isOpen && prevProps.isOpen) {
            this.closePopover();
        }

        if (
            this.props.link.childrenType === "FullWidthMenu" &&
            this.props.link.children &&
            this.props.link.children?.length > 0 &&
            !this.realTimeTriggered
        ) {
            this.loadRealTimePricingForAllFeatured();
        }
    }

    loadRealTimePricingForAllFeatured() {
        this.realTimeTriggered = true;
        const productIds = this.props.link.children?.reduce((result: string[], current) => {
            if (!current?.excludeFromNavigation) {
                (current.properties?.featuredProducts ? JSON.parse(current.properties?.featuredProducts) : []).forEach(
                    (item: any) => result.indexOf(item.id) === -1 && result.push(item.id),
                );
            }
            return result;
        }, []);
        // <xng-change> MIDPT-110: adding expansions to get fully api result for products
        this.props.loadProductInfoList({
            id: `navigation`,
            getProductCollectionParameter: {
                productIds,
                expand: [
                    "detail",
                    "specifications",
                    "content",
                    "images",
                    "documents",
                    "attributes",
                    "variantTraits",
                    "properties",
                ],
                includeAttributes: ["includeOnProduct"],
            },
        });
    }

    componentWillUnmount() {
        clearTimeout(this.timeoutId);
    }
    // </XNG-Changes>

    render() {
        const {
            styles,
            link,
            index,
            isOpen,
            theme: {
                breakpoints: { maxWidths },
            },
            // XNG-Change
            menuVariant,
        } = this.props;
        const downIcon = <StyledIcon src={ChevronDown} {...styles.menuItemIcon} />;
        const hasChildren = link.children && link.children.filter(child => !child.excludeFromNavigation).length > 0;
        const isCascading =
            hasChildren && (link.childrenType === "CascadingMenu" || link.childrenType === "CustomLinkList");

        const isMega = hasChildren && link.childrenType === "MegaMenu";
        // XNG-Changes
        const rightIcon = <StyledIcon src={ChevronRight} {...styles.menuItemIcon} />;
        const isFullWidth = hasChildren && link.childrenType === "FullWidthMenu";
        const hasInternalChildren = link.hasInternalChildren;

        let menuLink = (
            <Link
                className="menu-item"
                typographyProps={styles.menuItemTypography}
                color={styles.menuItemTypography?.color}
                {...styles.menuItem}
                href={link.url}
                target={link.openInNewWindow ? "_blank" : ""}
            >
                {link.title}
                {/* XNG-Changes */}
                {/* {isCascading && downIcon} */}
                {(isCascading || isFullWidth || hasInternalChildren) && downIcon}
            </Link>
        );
        if (isMega || link.url === "") {
            menuLink = (
                // XNG-Changes : add StyledWrapper
                <StyledWrapper>
                    <Link
                        className="menu-item"
                        typographyProps={styles.menuItemTypography}
                        color={styles.menuItemTypography?.color}
                        {...styles.menuItem}
                        // XNG-Changes
                        {...(link.titleLink && link.titleLink.url ? { href: link.titleLink.url } : "")}
                    >
                        {link.title}
                        {/* XNG-Changes */}
                        {/* {(isCascading || isMega) && downIcon} */}
                        {(isCascading || isMega || isFullWidth || hasInternalChildren) && downIcon}
                    </Link>
                </StyledWrapper>
            );
        }

        let menuItem = menuLink;

        if (
            (isCascading ||
                // XNG-Changes
                isFullWidth ||
                hasInternalChildren) &&
            // XNG-Changes
            link.children
        ) {
            const menuProps: { maxDepth?: number } = {};
            if (link.maxDepth) {
                menuProps.maxDepth = link.maxDepth;
            }
            // <XNG-Changes>
            if (isFullWidth) {
                menuItem = (
                    <Tooltip
                        passAsChildren={true}
                        triggerComponent={
                            <Clickable
                                className="menu-item"
                                color={styles.menuItemTypography?.color}
                                {...styles.menuItem}
                                href={link.url || link.titleLink?.url}
                                {...{ isOpen: this.state.isFullWidthOpened }}
                                target={link.openInNewWindow ? "_blank" : ""}
                            >
                                <Typography {...styles.menuItemTypography}>
                                    {link.title}
                                    {(isCascading || isFullWidth || hasInternalChildren) && downIcon}
                                </Typography>
                            </Clickable>
                        }
                        // onShow={() => {
                        //     this.setState({ ...this.state, isFullWidthOpened: true });
                        // }}
                        // onHide={() => {
                        //     this.setState({ ...this.state, isFullWidthOpened: false });
                        // }}
                        placement="bottom-end"
                        // delay={[100, 350]}
                        // offset={[0, 0]}
                        // showBlackArrow={true}
                        {...mainNavigationItemStyles.popup}
                        extendedStyles={{
                            tooltipContent: {
                                css: css`
                                    max-width: none;
                                    border-radius: 0;
                                    margin-left: -5px;
                                    width: 99vw;
                                    padding-top: 24px;
                                    padding-bottom: 30px;
                                `,
                            },
                        }}
                        // onClose={() => this.openMenu(0)}
                    >
                        <Page {...mainNavigationItemStyles.container}>
                            <StyledWrapper {...mainNavigationItemStyles.firstLevel}>
                                <FancyScroll showOverlay={true} bottomOnly={true} clickable={true}>
                                    {link.children
                                        ?.filter(item => !item.excludeFromNavigation)
                                        .map((item, idx) => {
                                            const hasChildren = item.children?.length;
                                            return (
                                                <Link
                                                    key={`popupLink_${item.title}`}
                                                    typographyProps={{ color: "secondary.contrast" }}
                                                    color="black"
                                                    href={item.url}
                                                    onClick={(e: any) => this.closeAllPopupsHandler(e)}
                                                    onMouseOver={() => this.openMenu(idx + 1, item)}
                                                    onMouseOut={() => this.closeMenu()}
                                                    {...({ opened: idx + 1 === this.state.current } as any)}
                                                    {...mainNavigationItemStyles.itemLink}
                                                >
                                                    {item.title}
                                                    {!!hasChildren && rightIcon}
                                                </Link>
                                            );
                                        })}
                                </FancyScroll>
                            </StyledWrapper>
                            {/* <StyledWrapper
                                css={css`
                                    display: flex;
                                    flex: 1;
                                `}
                            > */}
                            {/** EIS-1098: show category description on sub level not the promotion one */}
                            {/* {this.state.current ? (
                                    <NavigationSubLevel link={this.state.link} closeAllPopups={closeAllTippy} />
                                ) : (
                                    <NavigationPromotion />
                                )} */}
                            {/* </StyledWrapper> */}
                        </Page>
                    </Tooltip>
                );
            }
            // </XNG-Changes>
            // XNG-Changes : add condition
            else if (isCascading || hasInternalChildren) {
                menuItem = (
                    <Menu
                        className="cascading-menu"
                        data-test-selector={`mainNavigationItem_${index}`}
                        isOpen={isOpen || undefined}
                        descriptionId={`${link.title}_${index}`}
                        menuItems={link.children as any}
                        menuTrigger={menuLink}
                        displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                        {...menuProps}
                        {...styles.cascadingMenu}
                        // XNG-Changes
                        maxNumberOfLinks={this.props.link.maxNumberOfLinks}
                        menuVariant={menuVariant}
                    />
                );
            }
        }

        if (isMega) {
            menuItem = (
                <Popover
                    className="mega-menu"
                    zIndexKey="menu"
                    {...styles.megaMenu}
                    contentBodyProps={{
                        ...styles.megaMenu?.contentBodyProps,
                        _width: 1140,
                        as: "div",
                    }}
                    controlsId={`${index}_${link.title}`}
                    ref={this.popover}
                    positionFunction={(element: React.RefObject<HTMLUListElement | null>) => {
                        if (this.props.container.current?.offsetParent) {
                            const parentPosition = this.props.container.current.getBoundingClientRect();
                            const rect = element.current?.getBoundingClientRect();
                            const top = rect ? rect.top + (rect.height > 100 ? styles.menuHeight : rect.height) : 0;
                            const cartWidth = 150;
                            const breakpointMaxWidth = maxWidths.find((value: number, index: number) => {
                                return (
                                    parentPosition.width + cartWidth <= value &&
                                    parentPosition.width + cartWidth > maxWidths[index - 1]
                                );
                            });

                            const width = breakpointMaxWidth ? breakpointMaxWidth - 30 : parentPosition.width;

                            return {
                                top,
                                position: "fixed",
                                left: parentPosition.left,
                                width: `${width}px`,
                            };
                        }
                        return { position: "absolute" };
                    }}
                    popoverTrigger={menuLink}
                    insideRefs={[this.element]}
                >
                    <GridContainer
                        className="mega-menu-grid-container"
                        data-test-selector={`mainNavigationItem_${index}`}
                        {...styles.megaMenuGridContainer}
                        offsetProps={{ ...styles.megaMenuGridContainer?.offsetProps, as: "ul" }}
                    >
                        {link
                            .children!.filter(child => !child.excludeFromNavigation)
                            .map((child, index) => (
                                <GridItem
                                    className="mega-menu-grid-item"
                                    width={(12 / link.numberOfColumns!) as GridWidths}
                                    key={index}
                                    {...styles.megaMenuGridItem}
                                    as="li"
                                >
                                    <Link
                                        className="mega-menu-heading-link"
                                        {...styles.megaMenuHeading}
                                        href={child.url}
                                        onClick={this.closePopover}
                                    >
                                        {child.title}
                                    </Link>
                                    {child.children && (
                                        <ul data-test-selector={`linksFor_${child.title}`}>
                                            {child.children
                                                .filter(grandChild => {
                                                    if (grandChild.excludeFromNavigation) {
                                                        return false;
                                                    }
                                                    if (
                                                        grandChild.type === "ChangeCustomerPage" &&
                                                        !this.props.displayChangeCustomerLink
                                                    ) {
                                                        return false;
                                                    }
                                                    if (grandChild.type === "VmiUsersPage") {
                                                        const currentMode = getCookie(NavigationModeCookieName);
                                                        if (!currentMode || currentMode === "Storefront") {
                                                            return false;
                                                        }
                                                    }

                                                    return true;
                                                })
                                                .map((grandChild, index) => (
                                                    <li key={index}>
                                                        <Link
                                                            className="mega-menu-link"
                                                            {...styles.megaMenuLink}
                                                            onClick={this.closePopover}
                                                            href={grandChild.url}
                                                            data-test-selector={grandChild.title}
                                                        >
                                                            {grandChild.title}
                                                        </Link>
                                                    </li>
                                                ))}
                                        </ul>
                                    )}
                                </GridItem>
                            ))}
                    </GridContainer>
                </Popover>
            );
        }

        return menuItem;
    }
}

// XNG-Changes
// export default withTheme(MainNavigationItem);
export default connect(mapStateToProps, mapDispatchToProps)(withTheme(MainNavigationItem));
